import { useState, useCallback } from "react";
import { Target, CheckCircle, AlertTriangle, XCircle, Pencil } from "lucide-react";
import { Button, cn } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { useSLA, useUpdateSLAConfig } from "@/hooks/admin/useOperations";
import type { SLAStatus, SLAConfig } from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatMinutes(mins: number | null | undefined): string {
  if (mins == null || isNaN(mins)) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return "—";
  return `${new Intl.NumberFormat("fr-SN", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)} %`;
}

// ---------------------------------------------------------------------------
// SLA status card
// ---------------------------------------------------------------------------

interface SLAStatusCardProps {
  readonly status: SLAStatus;
  readonly config?: SLAConfig;
  readonly onEdit: (config: SLAConfig) => void;
}

function SLAStatusCard({ status, config, onEdit }: SLAStatusCardProps) {
  const isOk = status.status === "ok";
  const isWarning = status.status === "warning";

  const icon = isOk ? (
    <CheckCircle className="h-5 w-5 text-emerald-600" />
  ) : isWarning ? (
    <AlertTriangle className="h-5 w-5 text-amber-500" />
  ) : (
    <XCircle className="h-5 w-5 text-red-600" />
  );

  const ringColor = isOk
    ? "border-emerald-200"
    : isWarning
      ? "border-amber-200"
      : "border-red-200";

  const bgColor = isOk
    ? "bg-emerald-50"
    : isWarning
      ? "bg-amber-50"
      : "bg-red-50";

  const complianceColor = isOk
    ? "text-emerald-700"
    : isWarning
      ? "text-amber-700"
      : "text-red-700";

  return (
    <div className={cn("rounded-lg border bg-white p-5 shadow-sm", ringColor)}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className={cn("flex h-8 w-8 items-center justify-center rounded-md", bgColor)}>
            {icon}
          </div>
          <p className="font-medium text-stone-900">{status.label}</p>
        </div>
        {config && (
          <button
            onClick={() => onEdit(config)}
            className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            title="Modifier objectif"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <div>
          <p className="text-xs text-stone-400">Actuel</p>
          <p className="font-semibold text-stone-800">
            {formatMinutes(status.current_avg_minutes)}
          </p>
        </div>
        <div>
          <p className="text-xs text-stone-400">Objectif</p>
          <p className="font-semibold text-stone-800">
            {formatMinutes(status.target_minutes)}
          </p>
        </div>
        <div>
          <p className="text-xs text-stone-400">Conformité</p>
          <p className={cn("font-semibold", complianceColor)}>
            {formatPercent(status.compliance_percent)}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit target modal
// ---------------------------------------------------------------------------

interface EditTargetModalProps {
  readonly config: SLAConfig | null;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (id: string, target_minutes: number) => Promise<void>;
}

function EditTargetModal({ config, onClose, isSaving, onSave }: EditTargetModalProps) {
  const [targetMinutes, setTargetMinutes] = useState(
    config ? String(config.target_minutes) : "",
  );
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!config) return;
    const mins = parseInt(targetMinutes, 10);
    if (isNaN(mins) || mins <= 0) {
      setError("L'objectif doit être un nombre de minutes positif.");
      return;
    }
    setError(null);
    try {
      await onSave(config.id, mins);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [config, targetMinutes, onSave, handleClose]);

  if (!config) return null;

  return (
    <AdminModal
      open={!!config}
      onClose={handleClose}
      title={`Modifier objectif — ${config.label}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Objectif (en minutes)
          </label>
          <input
            type="number"
            min={1}
            value={targetMinutes}
            onChange={(e) => setTargetMinutes(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Ex. 60"
          />
          <p className="mt-1 text-xs text-stone-400">
            Actuel : {formatMinutes(config.target_minutes)}
          </p>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function SLA() {
  const [editConfig, setEditConfig] = useState<SLAConfig | null>(null);

  const { data, isLoading } = useSLA();
  const updateMutation = useUpdateSLAConfig();

  const statuses = data?.statuses ?? [];
  const configs = data?.configs ?? [];

  const getConfig = (metric: string): SLAConfig | undefined =>
    configs.find((c) => c.metric === metric);

  const handleSave = useCallback(
    async (id: string, target_minutes: number) => {
      await updateMutation.mutateAsync({ id, target_minutes });
    },
    [updateMutation],
  );

  const tableColumns: readonly ColumnDef<SLAStatus>[] = [
    {
      id: "label",
      header: "Indicateur",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.label}</span>
      ),
    },
    {
      id: "current_avg_minutes",
      header: "Actuel",
      cell: (row) => formatMinutes(row.current_avg_minutes),
    },
    {
      id: "target_minutes",
      header: "Objectif",
      cell: (row) => formatMinutes(row.target_minutes),
    },
    {
      id: "compliance_percent",
      header: "Conformité",
      cell: (row) => {
        const color =
          row.status === "ok"
            ? "text-emerald-700"
            : row.status === "warning"
              ? "text-amber-700"
              : "text-red-700";
        return <span className={`font-semibold ${color}`}>{formatPercent(row.compliance_percent)}</span>;
      },
    },
    {
      id: "status",
      header: "État",
      cell: (row) => {
        const label =
          row.status === "ok" ? "Conforme" : row.status === "warning" ? "Avertissement" : "Violation";
        const color =
          row.status === "ok"
            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : row.status === "warning"
              ? "bg-amber-100 text-amber-800 border-amber-200"
              : "bg-red-100 text-red-800 border-red-200";
        return (
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${color}`}>
            {label}
          </span>
        );
      },
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Target className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Niveaux de service (SLA)
            </h1>
            <p className="text-sm text-stone-500">Suivi de la conformité opérationnelle</p>
          </div>
        </div>

        {/* SLA cards */}
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-36 animate-pulse rounded-lg border border-stone-200 bg-stone-50"
              />
            ))}
          </div>
        ) : statuses.length === 0 ? (
          <div className="rounded-lg border border-stone-200 bg-white py-12 text-center">
            <p className="text-sm text-stone-400">Aucune configuration SLA disponible.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {statuses.map((status) => (
              <SLAStatusCard
                key={status.metric}
                status={status}
                config={getConfig(status.metric)}
                onEdit={setEditConfig}
              />
            ))}
          </div>
        )}

        {/* Table view */}
        {statuses.length > 0 && (
          <div>
            <h2 className="mb-3 font-display text-base font-semibold text-stone-800">
              Vue tabulaire
            </h2>
            <AdminDataTable
              columns={tableColumns}
              data={statuses}
              isLoading={isLoading}
              emptyMessage="Aucune donnée SLA."
            />
          </div>
        )}
      </div>

      <EditTargetModal
        config={editConfig}
        onClose={() => setEditConfig(null)}
        isSaving={updateMutation.isPending}
        onSave={handleSave}
      />
    </AdminLayout>
  );
}
